import type {
    ColorCombination,
    FabricColor,
    FrameColor,
    Product,
} from "../product.types";
import { sunProtectionsResponseSchema } from "../schemas/sun-protection.schema";
import { windowDecorationsResponseSchema } from "../schemas/window-decoration.schema";

type RawFabric = {
    id?: string | null;
    name?: string | null;
    hexColor?: string | null;
    price?: number | null;
    type?: "SUN_BLOCKING" | "TRANSLUCENT" | null;
    image?: { url?: string | null } | null;
};

type RawFrame = {
    id?: string | null;
    name?: string | null;
    hexColor?: string | null;
    price?: number | null;
};

type RawCombination = {
    id?: string | null;
    frameColorName?: string | null;
    fabricColorName?: string | null;
    irrelevant?: boolean | null;
    image?: { url?: string | null; alt?: string | null } | null;
};

function parseFabricColor(raw: RawFabric): FabricColor | null {
    if (!raw.id) return null;
    return {
        id: raw.id,
        name: raw.name ?? null,
        hexColor: raw.hexColor ?? null,
        price: raw.price ?? null,
        type: raw.type ?? null,
        image: raw.image ? { url: raw.image.url ?? null } : null,
    };
}

function parseFrameColor(raw: RawFrame): FrameColor | null {
    if (!raw.id) return null;
    return {
        id: raw.id,
        name: raw.name ?? null,
        hexColor: raw.hexColor ?? null,
        price: raw.price ?? null,
    };
}

function parseColorCombination(raw: RawCombination): ColorCombination | null {
    if (!raw.id) return null;
    return {
        id: raw.id,
        frameColorName: raw.frameColorName ?? null,
        fabricColorName: raw.fabricColorName ?? null,
        irrelevant: raw.irrelevant === true,
        image: raw.image
            ? { url: raw.image.url ?? null, alt: raw.image.alt ?? null }
            : null,
    };
}

export function parseSunProtections(raw: unknown): Product[] {
    const result = sunProtectionsResponseSchema.safeParse(raw);
    if (!result.success) return [];
    return result.data.SunProtections.docs.map((doc) => ({
        id: doc.id,
        productType: "SUN_PROTECTION" as const,
        name: doc.name,
        subtitle: doc.subtitle ?? null,
        image: doc.image ? { url: doc.image.url ?? null } : null,
        hasFrameColor: doc.hasFrameColor === true,
        fabricColors: (doc.fabricColors ?? [])
            .map(parseFabricColor)
            .filter((c): c is FabricColor => c !== null),
        frameColors: (doc.frameColors ?? [])
            .map(parseFrameColor)
            .filter((c): c is FrameColor => c !== null),
        colorCombinations: (doc.colorCombinations ?? [])
            .map(parseColorCombination)
            .filter((c): c is ColorCombination => c !== null),
    }));
}

export function parseWindowDecorations(raw: unknown): Product[] {
    const result = windowDecorationsResponseSchema.safeParse(raw);
    if (!result.success) return [];
    return result.data.WindowDecorations.docs.map((doc) => ({
        id: doc.id,
        productType: "WINDOW_DECORATION" as const,
        name: doc.name,
        subtitle: doc.subtitle ?? null,
        image: doc.image ? { url: doc.image.url ?? null } : null,
        hasFrameColor: false,
        fabricColors: (doc.colors ?? [])
            .map(parseFabricColor)
            .filter((c): c is FabricColor => c !== null),
        frameColors: [],
        colorCombinations: [],
    }));
}
